import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCircleInfo, faTimes } from "@fortawesome/free-solid-svg-icons";

import { usePersistedFlag } from "../../usePersistedFlag";
import "./InfoPopover.css";

const CLOSE_MS = 160;
const SECTION_MS = 300;

const BUBBLE_MAX_WIDTH = 440;
const BUBBLE_MIN_HEIGHT = 220;
const VIEWPORT_GUTTER = 16;
const TRIGGER_GAP = 18; // leaves ~4px between the tail's tip and the trigger
// A tail closer than this to a side of the bubble would run into its rounded corner. It then
// becomes a corner tail instead: that side of the bubble runs straight up into it, to a rounded
// tip right above the trigger's centre.
const TAIL_CORNER_ZONE = 30;

// Corner tail, drawn for the right corner (mirrored for the left): 12px tall and 20px wide, a
// ~31° slope with a rounded tip (r 6) and a curve (6px) where it meets the bubble's top edge.
// The edge shape is the bubble's 4px border (--pop-border); the fill is that shape inset by the
// border width. Both run 2px into the bubble (over its border and fill respectively), so no
// edges have to line up exactly. The viewBox starts at x -6 for the base curve; x 20 is the tip.
// Below the rounded tip, the straight side is drawn by CSS (see .info-pop.tail-corner-*::after):
// an SVG can land half a pixel off from the bubble's own border, a CSS box can't. The fill runs
// under that side (to x 18) so there's never a gap between the two.
const CORNER_TAIL = {
    height: 12,
    viewBox: "-6 0 26 18",
    edge: "M-6 16 L-6 12 Q0 12 5.14 8.91 L14.86 3.09 Q20 0 20 6 L20 16 Z",
    fill: "M-4.89 18 L-4.89 16 Q1.11 16 6.25 12.91 L12.57 9.12 Q16 7.06 16 11.06 L18 11.06 L18 18 Z",
};
const CORNER_TIP_GAP = 4;

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

let popoverSeq = 0;

const prefersReducedMotion = () =>
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const SectionContext = React.createContext(null);

export const InfoPopoverSection = ({ title, defaultOpen = false, children }) => {
    const accordion = React.useContext(SectionContext);
    const [standaloneOpen, setStandaloneOpen] = useState(defaultOpen);
    const sectionRef = useRef(null);

    const open = accordion ? accordion.openId === title : standaloneOpen;

    useLayoutEffect(() => {
        if (!defaultOpen || !accordion) return;
        accordion.setOpenId((current) => (current === null ? title : current));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const scrollIntoViewAfterExpanding = () => {
        window.setTimeout(() => {
            if (sectionRef.current) {
                sectionRef.current.scrollIntoView({
                    block: "nearest",
                    behavior: prefersReducedMotion() ? "auto" : "smooth",
                });
            }
        }, SECTION_MS + 20);
    };

    const toggle = () => {
        const willOpen = !open;
        if (accordion) accordion.setOpenId(willOpen ? title : null);
        else setStandaloneOpen(willOpen);
        if (willOpen) scrollIntoViewAfterExpanding();
    };

    return (
        <section ref={sectionRef} className={`info-pop-section${open ? " open" : ""}`}>
            <button
                type="button"
                className="info-pop-section-toggle"
                aria-expanded={open}
                onClick={toggle}
            >
                <span>{title}</span>
                <FontAwesomeIcon className="info-pop-chevron" icon={faChevronDown} />
            </button>
            <div className="info-pop-section-panel">
                <div className="info-pop-section-inner">{children}</div>
            </div>
        </section>
    );
};

const InfoPopover = ({
    storageKey,
    title = "About this page",
    label = "About this page",
    // "end" (default): the trigger sits at the right end of the content and the bubble opens to
    // its left, with a corner tail; "center": the bubble is centred under the trigger.
    align = "end",
    className = "",
    children,
}) => {
    const [open, setOpen] = useState(false);
    const [closing, setClosing] = useState(false);
    const [placement, setPlacement] = useState(null);
    const [openSectionId, setOpenSectionId] = useState(null);
    const [seen, markSeen] = usePersistedFlag(storageKey);

    const accordion = React.useMemo(
        () => ({ openId: openSectionId, setOpenId: setOpenSectionId }),
        [openSectionId]
    );

    const triggerRef = useRef(null);
    const bubbleRef = useRef(null);
    const closeRef = useRef(null);
    const autoOpened = useRef(false);
    const openRef = useRef(false);
    const titleId = useRef(`info-pop-title-${(popoverSeq += 1)}`).current;

    const mounted = open || closing;

    const closePopover = useCallback(() => {
        if (!openRef.current) return;
        openRef.current = false;
        setOpen(false);
        setClosing(true);
        if (triggerRef.current) triggerRef.current.focus({ preventScroll: true });
    }, []);

    const openPopover = useCallback(() => {
        openRef.current = true;
        setClosing(false);
        setOpen(true);
    }, []);

    useEffect(() => {
        if (seen || autoOpened.current) return;
        autoOpened.current = true;
        markSeen();
        openPopover();
    }, [seen, markSeen, openPopover]);

    const position = useCallback(() => {
        if (!triggerRef.current) return;
        const trigger = triggerRef.current.getBoundingClientRect();
        const vw = document.documentElement.clientWidth;
        let width = Math.min(BUBBLE_MAX_WIDTH, vw - VIEWPORT_GUTTER * 2);

        // Whole pixels, so the bubble's border and the (SVG) corner tail line up exactly
        const centre = Math.round(trigger.left + trigger.width / 2);
        width = Math.round(width);
        let left = Math.max(
            VIEWPORT_GUTTER,
            Math.min(align === "end" ? centre - width : centre - width / 2, vw - VIEWPORT_GUTTER - width)
        );
        // A corner tail puts that side of the bubble right under the trigger's centre
        let tailCorner = "";
        if (align === "end" || centre - left > width - TAIL_CORNER_ZONE) {
            tailCorner = "right";
            width = Math.min(width, centre - VIEWPORT_GUTTER);
            left = centre - width;
        } else if (centre - left < TAIL_CORNER_ZONE) {
            tailCorner = "left";
            width = Math.min(width, vw - VIEWPORT_GUTTER - centre);
            left = centre;
        }
        left = Math.round(left);
        const top = Math.round(trigger.bottom + (tailCorner ? CORNER_TAIL.height + CORNER_TIP_GAP : TRIGGER_GAP));

        const next = {
            width,
            left,
            top,
            tailX: centre - left,
            tailCorner,
            maxHeight: Math.max(
                BUBBLE_MIN_HEIGHT,
                window.innerHeight - top - VIEWPORT_GUTTER
            ),
        };
        setPlacement((prev) =>
            prev && Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next
        );
    }, [align]);

    useLayoutEffect(() => {
        if (!mounted) return undefined;
        const body = document.body;
        const prevOverflow = body.style.overflow;
        const prevPadding = body.style.paddingRight;
        const widthBefore = document.documentElement.clientWidth;

        body.style.overflow = "hidden";
        const reclaimed = document.documentElement.clientWidth - widthBefore;
        if (reclaimed > 0) {
            const current = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
            body.style.paddingRight = `${current + reclaimed}px`;
        }

        return () => {
            body.style.overflow = prevOverflow;
            body.style.paddingRight = prevPadding;
        };
    }, [mounted]);

    useLayoutEffect(() => {
        if (!open) return undefined;
        position();
        window.addEventListener("resize", position);
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(position) : null;
        if (observer) observer.observe(document.documentElement);
        return () => {
            window.removeEventListener("resize", position);
            if (observer) observer.disconnect();
        };
    }, [open, position]);

    useEffect(() => {
        if (!open || !closeRef.current) return;
        closeRef.current.focus({ preventScroll: true });
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                closePopover();
                return;
            }
            if (e.key !== "Tab" || !bubbleRef.current) return;
            const items = Array.from(bubbleRef.current.querySelectorAll(FOCUSABLE))
                .filter((el) => !el.disabled && el.offsetParent !== null);
            if (items.length === 0) return;
            const first = items[0];
            const last = items[items.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open, closePopover]);

    useEffect(() => {
        if (!closing) return undefined;
        const t = setTimeout(() => setClosing(false), CLOSE_MS + 120);
        return () => clearTimeout(t);
    }, [closing]);

    const handleAnimationEnd = (e) => {
        if (e.target === bubbleRef.current && closing) setClosing(false);
    };

    const overlay = (
        <>
            <div
                className={`info-pop-backdrop${closing ? " closing" : ""}`}
                onClick={closePopover}
            />
            <div
                ref={bubbleRef}
                className={`info-pop${closing ? " closing" : ""}${
                    placement?.tailCorner ? ` tail-corner-${placement.tailCorner}` : ""}`}
                style={{
                    top: `${placement ? placement.top : 0}px`,
                    left: `${placement ? placement.left : 0}px`,
                    width: `${placement ? placement.width : BUBBLE_MAX_WIDTH}px`,
                    maxHeight: placement ? `${placement.maxHeight}px` : undefined,
                    "--pop-tail-x": `${placement ? placement.tailX : BUBBLE_MAX_WIDTH / 2}px`,
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onAnimationEnd={handleAnimationEnd}
            >
                {placement?.tailCorner ? (
                    <svg
                        className={`info-pop-tail-corner at-${placement.tailCorner}`}
                        viewBox={CORNER_TAIL.viewBox}
                        aria-hidden="true"
                    >
                        <path className="info-pop-tail-edge" d={CORNER_TAIL.edge} />
                        <path className="info-pop-tail-fill" d={CORNER_TAIL.fill} />
                    </svg>
                ) : (
                    <span className="info-pop-tail" aria-hidden="true" />
                )}
                <div className="info-pop-header">
                    <span className="info-pop-title" id={titleId}>{title}</span>
                    <button
                        ref={closeRef}
                        type="button"
                        className="info-pop-close"
                        aria-label="Close"
                        onClick={closePopover}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="info-pop-body">
                    <SectionContext.Provider value={accordion}>{children}</SectionContext.Provider>
                </div>
            </div>
        </>
    );

    return (
        <div className={`info-pop-trigger-row ${className}`}>
            <button
                ref={triggerRef}
                type="button"
                className={`info-pop-trigger${mounted ? " active" : ""}`}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label={label}
                title={label}
                onClick={() => (open ? closePopover() : openPopover())}
            >
                {/* Circle and (i) in one SVG, so they're rendered together and the (i) stays exactly
                    centred at any position or zoom (a CSS background and a separate icon can each
                    snap to a different device pixel) */}
                <svg className="info-pop-trigger-graphic" viewBox="0 0 28 28" aria-hidden="true">
                    <circle className="info-pop-trigger-bg" cx="14" cy="14" r="14" />
                    {/* The group, not the nested svg, gets the (scale) transform: it scales cleanly
                        around its own centre */}
                    <g className="info-pop-trigger-icon">
                        <svg x="7" y="7" width="14" height="14"
                             viewBox={`0 0 ${faCircleInfo.icon[0]} ${faCircleInfo.icon[1]}`}>
                            <path d={faCircleInfo.icon[4]} />
                        </svg>
                    </g>
                </svg>
            </button>
            {mounted && createPortal(overlay, document.body)}
        </div>
    );
};

export default InfoPopover;
